import { useMemo, useState } from "react";
import { Clock } from "lucide-react";
import "./RetirementCalculator.css";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.round(n));

function sipFV(P, annualRate, years) {
  const r = annualRate / 12 / 100;
  const n = years * 12;
  if (r === 0) return P * n;
  return P * (((Math.pow(1 + r, n) - 1) / r) * (1 + r));
}

export default function RetirementCalculator() {
  const [monthly, setMonthly] = useState(15000);
  const [rate, setRate] = useState(11);
  const [years, setYears] = useState(25);
  const [existing, setExisting] = useState(200000);

  const { corpus, fromSip, fromExisting, invested } = useMemo(() => {
    const sipPart = sipFV(monthly, rate, years);
    const lumpPart = existing * Math.pow(1 + rate / 100, years);
    return {
      fromSip: sipPart,
      fromExisting: lumpPart,
      corpus: sipPart + lumpPart,
      invested: monthly * years * 12 + existing,
    };
  }, [monthly, rate, years, existing]);

  return (
    <section className="calc-card" id="retirement">
      <div className="calc-card__head">
        <div className="calc-card__icon">
          <Clock size={22} />
        </div>
        <div>
          <h2>Retirement Calculator</h2>
          <p>Build a retirement corpus with SIPs plus your existing savings.</p>
        </div>
      </div>

      <div className="calc-layout">
        <div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="ret-sip">Monthly SIP</label>
              <input
                id="ret-sip"
                className="calc-field__input"
                type="number"
                min={500}
                max={500000}
                step={500}
                value={monthly}
                onChange={(e) => setMonthly(Number(e.target.value) || 0)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={500}
              max={100000}
              step={500}
              value={Math.min(monthly, 100000)}
              onChange={(e) => setMonthly(Number(e.target.value))}
            />
          </div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="ret-exist">Existing savings</label>
              <input
                id="ret-exist"
                className="calc-field__input"
                type="number"
                min={0}
                max={50000000}
                step={10000}
                value={existing}
                onChange={(e) => setExisting(Number(e.target.value) || 0)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={0}
              max={5000000}
              step={10000}
              value={Math.min(existing, 5000000)}
              onChange={(e) => setExisting(Number(e.target.value))}
            />
          </div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="ret-rate">Expected return (% p.a.)</label>
              <input
                id="ret-rate"
                className="calc-field__input"
                type="number"
                min={1}
                max={20}
                step={0.5}
                value={rate}
                onChange={(e) => setRate(Number(e.target.value) || 0)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={1}
              max={20}
              step={0.5}
              value={rate}
              onChange={(e) => setRate(Number(e.target.value))}
            />
          </div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="ret-years">Years to retirement</label>
              <input
                id="ret-years"
                className="calc-field__input"
                type="number"
                min={1}
                max={45}
                value={years}
                onChange={(e) => setYears(Number(e.target.value) || 1)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={1}
              max={45}
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
            />
          </div>
        </div>

        <div className="calc-results">
          <div className="calc-result">
            <span>Total invested</span>
            <strong>{formatINR(invested)}</strong>
          </div>
          <div className="calc-result">
            <span>From SIP</span>
            <strong>{formatINR(fromSip)}</strong>
          </div>
          <div className="calc-result">
            <span>From existing</span>
            <strong>{formatINR(fromExisting)}</strong>
          </div>
          <div className="calc-result calc-result--accent">
            <span>Retirement corpus</span>
            <strong>{formatINR(corpus)}</strong>
          </div>
          <p className="calc-note">
            SIP accumulation plus lumpsum growth of existing savings to target
            date.
          </p>
        </div>
      </div>
    </section>
  );
}
